import { useMemo, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@ouropix/ui';
import { Button } from '@ouropix/ui';
import { Badge } from '@ouropix/ui';
import { Label } from '@ouropix/ui';
import { Input } from '@ouropix/ui';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@ouropix/ui';
import {
  History,
  ChevronLeft,
  ChevronRight,
  Filter,
  ArrowLeft,
  Shield,
} from 'lucide-react';

export const Route = createFileRoute('/_admin/saques/historico')({
  component: SaquesHistoricoPage,
});

interface WithdrawalItem {
  id: string;
  user_id: string;
  user_masked: string;
  cpf_masked: string;
  kyc_level: number;
  amount: string;
  pix_key: string;
  pix_key_type: string;
  status: string;
  device_risk_score: number;
  rejection_reason: string;
  reviewed_by: string;
  reviewed_at: string;
  provider: string;
  external_reference: string;
  created_at: string;
}

function mapApiWithdrawal(w: any): WithdrawalItem {
  return {
    id: w.id,
    user_id: w.userId ?? w.user_id ?? '',
    user_masked: w.userMasked ?? w.user_masked ?? '***',
    cpf_masked: w.cpfMasked ?? w.cpf_masked ?? '',
    kyc_level: Number(w.kycLevel ?? w.kyc_level ?? 0),
    amount: w.amount ?? '0',
    pix_key: w.pixKey ?? w.pix_key ?? '',
    pix_key_type: w.pixKeyType ?? w.pix_key_type ?? '',
    status: w.status ?? '',
    device_risk_score: Number(w.deviceRiskScore ?? w.device_risk_score ?? 0),
    rejection_reason: w.rejectionReason ?? w.rejection_reason ?? '',
    reviewed_by: w.reviewedBy ?? w.reviewed_by ?? '',
    reviewed_at: w.reviewedAt ?? w.reviewed_at ?? '',
    provider: w.provider ?? '',
    external_reference: w.externalReference ?? w.external_reference ?? '',
    created_at: w.createdAt ?? w.created_at ?? '',
  };
}

function formatCurrency(value: string | number): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(num);
}

function formatDate(iso: string): string {
  if (!iso) return '--';
  try {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function statusBadge(status: string) {
  switch (status) {
    case 'approved':
    case 'processing':
      return (
        <Badge variant="outline" className="border-blue-500 text-blue-600 text-xs">
          {status === 'processing' ? 'Processando' : 'Aprovado'}
        </Badge>
      );
    case 'completed':
      return (
        <Badge variant="outline" className="border-green-500 text-green-600 text-xs">
          Concluido
        </Badge>
      );
    case 'rejected':
      return (
        <Badge variant="outline" className="border-red-500 text-red-600 text-xs">
          Rejeitado
        </Badge>
      );
    case 'failed':
      return (
        <Badge variant="outline" className="border-destructive text-destructive text-xs">
          Falhou
        </Badge>
      );
    case 'escalated':
      return (
        <Badge variant="outline" className="border-purple-500 text-purple-500 text-xs">
          Escalado
        </Badge>
      );
    default:
      return <Badge variant="secondary" className="text-xs">{status}</Badge>;
  }
}

const PAGE_SIZE = 20;

// Status que representam saques ja processados (historico)
const HISTORY_STATUSES = 'processing,approved,completed,rejected,failed,escalated';

interface Filters {
  status: string;
  date_from: string;
  date_to: string;
  min_amount: string;
  max_amount: string;
  user_id: string;
}

const EMPTY_FILTERS: Filters = {
  status: '',
  date_from: '',
  date_to: '',
  min_amount: '',
  max_amount: '',
  user_id: '',
};

function SaquesHistoricoPage() {
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [draftFilters, setDraftFilters] = useState<Filters>(EMPTY_FILTERS);

  const queryParams = useMemo(() => {
    const params: Record<string, string | number> = {
      page: currentPage,
      limit: PAGE_SIZE,
      status: filters.status || HISTORY_STATUSES,
    };
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;
    if (filters.min_amount) params.min_amount = filters.min_amount;
    if (filters.max_amount) params.max_amount = filters.max_amount;
    if (filters.user_id) params.user_id = filters.user_id;
    return params;
  }, [currentPage, filters]);

  const { data, isLoading } = useQuery<{
    withdrawals: WithdrawalItem[];
    total: number;
  }>({
    queryKey: ['admin-withdrawals-historico', queryParams],
    queryFn: async () => {
      const res = await api.get('/admin/withdrawals/pending', {
        params: queryParams,
      });
      const raw = res.data?.withdrawals ?? res.data?.data ?? [];
      const total = res.data?.total ?? res.data?.pagination?.total ?? raw.length ?? 0;
      return {
        withdrawals: (Array.isArray(raw) ? raw : []).map(mapApiWithdrawal),
        total,
      };
    },
  });

  const withdrawals = data?.withdrawals ?? [];
  const totalPages = Math.max(1, Math.ceil((data?.total ?? 0) / PAGE_SIZE));

  const applyFilters = () => {
    setFilters(draftFilters);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setDraftFilters(EMPTY_FILTERS);
    setFilters(EMPTY_FILTERS);
    setCurrentPage(1);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Link to="/saques">
          <Button variant="ghost" size="sm" className="gap-1">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <History className="h-5 w-5 text-primary" />
        <div>
          <h1 className="text-2xl font-bold">Historico de Saques</h1>
          <p className="text-muted-foreground text-sm">
            Saques processados, concluidos, rejeitados e falhados
          </p>
        </div>
      </div>

      {/* Filtros */}
      <div className="rounded-md border bg-card p-4 space-y-4">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Filter className="h-4 w-4" />
          Filtros
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Status</Label>
            <Select
              value={draftFilters.status || 'all'}
              onValueChange={(v) =>
                setDraftFilters((f) => ({
                  ...f,
                  status: v === 'all' ? '' : v,
                }))
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos historico</SelectItem>
                <SelectItem value="processing">Processando</SelectItem>
                <SelectItem value="completed">Concluido</SelectItem>
                <SelectItem value="rejected">Rejeitado</SelectItem>
                <SelectItem value="failed">Falhou</SelectItem>
                <SelectItem value="escalated">Escalado</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">De</Label>
            <Input
              type="date"
              value={draftFilters.date_from}
              onChange={(e) =>
                setDraftFilters((f) => ({ ...f, date_from: e.target.value }))
              }
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Ate</Label>
            <Input
              type="date"
              value={draftFilters.date_to}
              onChange={(e) =>
                setDraftFilters((f) => ({ ...f, date_to: e.target.value }))
              }
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Valor minimo (R$)</Label>
            <Input
              type="number"
              inputMode="decimal"
              min="0"
              step="0.01"
              placeholder="0"
              value={draftFilters.min_amount}
              onChange={(e) =>
                setDraftFilters((f) => ({ ...f, min_amount: e.target.value }))
              }
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Valor maximo (R$)</Label>
            <Input
              type="number"
              inputMode="decimal"
              min="0"
              step="0.01"
              placeholder="10000"
              value={draftFilters.max_amount}
              onChange={(e) =>
                setDraftFilters((f) => ({ ...f, max_amount: e.target.value }))
              }
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">ID do usuario (UUID)</Label>
            <Input
              type="text"
              placeholder="uuid..."
              value={draftFilters.user_id}
              onChange={(e) =>
                setDraftFilters((f) => ({ ...f, user_id: e.target.value.trim() }))
              }
            />
          </div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={applyFilters}>
            Aplicar
          </Button>
          <Button size="sm" variant="outline" onClick={clearFilters}>
            Limpar
          </Button>
        </div>
      </div>

      {/* Tabela */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Usuario</TableHead>
              <TableHead className="hidden md:table-cell">CPF</TableHead>
              <TableHead>Valor</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="hidden lg:table-cell">Provider</TableHead>
              <TableHead className="hidden lg:table-cell">Ref. Externa</TableHead>
              <TableHead className="hidden md:table-cell">Motivo Rejeicao</TableHead>
              <TableHead className="hidden lg:table-cell">Revisado em</TableHead>
              <TableHead className="hidden sm:table-cell">Criado em</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                  Carregando...
                </TableCell>
              </TableRow>
            ) : withdrawals.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-12 text-muted-foreground">
                  <Shield className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  Nenhum registro encontrado
                </TableCell>
              </TableRow>
            ) : (
              withdrawals.map((w) => (
                <TableRow key={w.id}>
                  <TableCell>
                    <span className="font-medium">{w.user_masked}</span>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <span className="font-mono text-sm text-muted-foreground">
                      {w.cpf_masked || '--'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="font-mono font-semibold">
                      {formatCurrency(w.amount)}
                    </span>
                  </TableCell>
                  <TableCell>
                    {statusBadge(w.status)}
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    {w.provider ? (
                      <Badge variant="secondary" className="text-xs uppercase">
                        {w.provider}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground text-xs">--</span>
                    )}
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <span className="font-mono text-xs text-muted-foreground break-all">
                      {w.external_reference
                        ? w.external_reference.slice(0, 16) + (w.external_reference.length > 16 ? '...' : '')
                        : '--'}
                    </span>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    {w.rejection_reason ? (
                      <span className="text-xs text-destructive">
                        {w.rejection_reason.slice(0, 40)}{w.rejection_reason.length > 40 ? '...' : ''}
                      </span>
                    ) : (
                      <span className="text-muted-foreground text-xs">--</span>
                    )}
                  </TableCell>
                  <TableCell className="hidden lg:table-cell text-sm text-muted-foreground">
                    {formatDate(w.reviewed_at)}
                  </TableCell>
                  <TableCell className="hidden sm:table-cell text-sm text-muted-foreground">
                    {formatDate(w.created_at)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Paginacao */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Pagina {currentPage} de {totalPages}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage((p) => p - 1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm">
              {currentPage} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
